import React from 'react';
import {HealthCheck, HealthStatus} from "../../types/HealthCheck";
import {MonitorService} from "../../services/monitor-service";
import Loader from "../loader/Loader";
import {Container, Row, Col, Card, Table, Alert} from "react-bootstrap";
import Notification from "../../common/Notification";
import classNames from "classnames";

import "./Monitoring.scss";

type MonitoringState = {
  // First load
  hasLoaded: boolean
  // Sequential updates
  isUpdating: boolean
  status?: HealthStatus
}

// 10 seconds
const TIMER_INTERVAL = 10000;

export default class Monitoring extends React.Component<{}, MonitoringState> {

  private updateInterval: any;

  constructor(props: {}) {
    super(props);
    this.state = {
      hasLoaded: false,
      isUpdating: false
    };
  }

  componentDidMount() {
    this.fetchHealthStatus();
    // Register a interval timer for polling health updates
    this.updateInterval = setInterval(() => {
      // Avoid in case of pending updates, the health endpoint can be quite slow sometimes
      if (!this.state.isUpdating) {
        this.fetchHealthStatus();
      }
    }, TIMER_INTERVAL);
  }

  componentWillUnmount() {
    clearInterval(this.updateInterval);
  }

  fetchHealthStatus() {
    this.setState({
      isUpdating: true
    });
    MonitorService.getHealthStatus(true).then((status: HealthStatus) => {
      this.setState({status});
    }).catch(() => {
      Notification.error({message: 'Cannot load health status'});
    }).finally(() => {
      this.setState({
        isUpdating: false,
        hasLoaded: true
      });
    });
  }

  render() {
    if (!this.state.hasLoaded) {
      return <Loader size="lg" block={true}/>;
    }

    if (!this.state.status) {
      return <Alert variant="danger">Cannot load health status</Alert>;
    }

    const cards = this.state.status.healthChecks.map(healthCheck => {
      return (
        <Col xl={4} lg={6} md={12} sm={12} xs={12} key={healthCheck.id}>
          <HealthCheckCard healthCheck={healthCheck}/>
        </Col>
      );
    });

    return (
      <div className="Monitoring">
        <Container className="p-0" fluid>
          <Row className="OverallStatusRow">
            <Col>
              <Alert variant={getBorderVariant(this.state.status.status)}
                     className="OverallStatusAlert">
                Overall status: {this.state.status.status}
                {
                  this.state.isUpdating ? <Loader className="MonitoringUpdateLoader"/> : null
                }
              </Alert>
            </Col>
          </Row>

          <Row className="HealthChecksRow">
            {cards}
          </Row>
        </Container>
      </div>
    );
  }
}

function HealthCheckCard(props: { healthCheck: HealthCheck }) {
  const borderStyle = getBorderVariant(props.healthCheck.status);
  const classes = classNames('HealthCheckCard', {
    'HealthCheckSuccess': props.healthCheck.status === 'OK',
    'HealthCheckWarning': props.healthCheck.status === 'WARNING',
    'HealthCheckError': props.healthCheck.status === 'ERROR'
  });
  return (
    <Card className={classes} border={borderStyle}>
      <Card.Body>
        <Card.Title>{props.healthCheck.name || props.healthCheck.id}</Card.Title>
        <Table hover size="sm" className="m-0">
          <tbody>
          <tr>
            <td>Name</td>
            <td>{props.healthCheck.name}</td>
          </tr>
          <tr>
            <td>Status</td>
            <td>{props.healthCheck.status}</td>
          </tr>
          <tr>
            <td>Severity</td>
            <td>{props.healthCheck.severity}</td>
          </tr>
          <tr>
            <td>Impact</td>
            <td>{props.healthCheck.impact}</td>
          </tr>
          <tr>
            <td>Message</td>
            <td>{props.healthCheck.message}</td>
          </tr>
          <tr>
            <td>ID</td>
            <td>{props.healthCheck.id}</td>
          </tr>
          <tr>
            <td>Type</td>
            <td>{props.healthCheck.type}</td>
          </tr>
          <tr>
            <td>Description</td>
            <td>{props.healthCheck.description}</td>
          </tr>
          <tr>
            <td>Troubleshooting</td>
            <td>
              <a href={props.healthCheck.troubleshooting} target="_blank" rel="noopener noreferrer">
                {props.healthCheck.troubleshooting}
              </a>
            </td>
          </tr>
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

function getBorderVariant(status: string): 'success' | 'warning' | 'danger' {
  if (status === 'ERROR') {
    return 'danger'
  } else if (status === 'WARNING') {
    return 'warning';
  }
  return 'success';
}
